import React from 'react'
import {DEIcon, ENIcon, NLIcon} from '@yaak/components/assets'
import style from './style.less'
import {Partner, LicenseType, User} from '@yaak/components/services/api/api'
import {isValidPhoneNumber} from '../../helpers/validation'
import {containsSpaces, isEmailValid} from '../../helpers/users'
import LicensesWithDetailsInput from '@yaak/components/src/LicensesWithDetailsInput/LicensesWithDetailsInput'

export const firstNameInput = {
  name: 'firstName',
  title: 'First name',
  required: true,
  placeHolderText: 'e.g. Bugs',
}

export const lastNameInput = {
  name: 'lastName',
  title: 'Last name',
  required: true,
  placeHolderText: 'e.g. Bunny',
}

export const emailInput = {
  name: 'email',
  title: 'Email',
  required: true,
  placeHolderText: 'e.g. bugsbunny@gmail.com',
  validate: (email: string) => containsSpaces(email) && isEmailValid(email),
  errorText: 'Please provide a valid email.',
}

export const phoneNumberInput = {
  name: 'phoneNumber',
  title: 'Mobile',
  required: false,
  placeHolderText: '+4901234567890',
  description:
    '(e.g. +4901234567890).',
  validate: (number: string) => isValidPhoneNumber(number) !== null,
  errorText: 'Please provide a valid phone number (e.g. +4901234567890).',
}

export const partnerInput = (partners: Array<Partner>) => ({
  name: 'partnerId',
  title: 'Partner',
  required: true,
  placeHolderText: 'Select a partner',
  description: 'Be sure you have already created the partner entity.',
  selections: partners.map((partner) => ({
    value: partner.id,
    name: partner.name,
    item: <div>{partner.name}</div>,
  })),
})

export const preferredLanguageInput = {
  name: 'preferredLanguage',
  title: 'Language',
  required: true,
  placeHolderText: 'Select a language',
  description: 'This is the partner’s primary contact language.',
  selections: [
    {
      name: 'English',
      value: 'en',
      item: (
        <div className={style.languageItem}>
          <ENIcon />
          {'English'}
        </div>
      ),
    },
    {
      name: 'Deutsch',
      value: 'de',
      item: (
        <div className={style.languageItem}>
          <DEIcon />
          {'Deutsch'}
        </div>
      ),
    },
    {
      name: 'Nederlands',
      value: 'nl',
      item: (
        <div className={style.languageItem}>
          <NLIcon />
          {'Nederlands'}
        </div>
      ),
    },
  ],
}

export const createStudentLicensesInput = (licenseTypes: LicenseType[]) => ({
  name: 'licenses',
  title: 'Driving Licenses',
  required: true,
  renderInput: (onChange: (name: string, value: any) => void, defaultValue?: any) => (
    <LicensesWithDetailsInput
      licenseTypes={licenseTypes}
      fieldName="licenses"
      onChange={onChange}
      defaultValue={defaultValue}
    />
  ),
})

export const createDrivingLicensesInput = (licenseTypes: LicenseType[]) => {
  const allLicenseSelections = licenseTypes.map((licenseType) => ({
    value: licenseType.id,
    name: licenseType.code,
    item: (props: React.HTMLAttributes<HTMLDivElement>) => (
      <div {...props} className={`${props.className} ${style.dropdownItem}`}>
        {licenseType.code}
      </div>
    ),
  }))

  const bLicense = licenseTypes.find((license) => license.code === 'B')
  const defaultValue = bLicense ? [bLicense.id] : (licenseTypes.length > 0 ? [licenseTypes[0].id] : [])

  return {
    name: 'licenses',
    title: 'Driving Licenses',
    required: true,
    multiple: true,
    value: defaultValue,
    selections: allLicenseSelections,
  }
}

export const statusInput = {
  name: 'active',
  title: 'Status',
  required: false,
}

export const simEnabledInput = {
  name: 'simEnabled',
  title: 'Sim-enabled',
  required: false,
}

export const createDefaultInstructorInput = (instructors: User[], currentDefaultInstructorId?: string | null) => {
  const instructorSelections = instructors.map((instructor) => ({
    value: instructor.id,
    name: `${instructor.firstName} ${instructor.lastName}`,
    item: (
      <div className={style.dropdownItem}>
        {`${instructor.firstName} ${instructor.lastName}`}
      </div>
    ),
  }))

  return {
    name: 'defaultInstructor',
    title: 'Default Instructor',
    required: false,
    multiple: false,
    value: currentDefaultInstructorId || null,
    placeHolderText: 'Select an instructor (optional)',
    selections: instructorSelections,
    description: 'Optionally assign a default instructor for this student.',
  }
}
